import React, { useEffect, useReducer, useRef } from 'react';
import { css } from '@linaria/atomic';
import { cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { PauseIcon } from '../../icon/icons/pause';
import { PlayIcon } from '../../icon/icons/play';
import { IconButton } from '../icon-button/icon-button.component';
import { SpinningLoader } from '../loader/spinning-loader';
import { Text } from '../text';
import {
  audioScrubberBaseStyle,
  thumbVisibleStyle,
  filledTrackStyle,
  emptyTrackStyle,
  errorStyle,
  compactStyle,
} from './audio-scrubber.styles';
import { AudioState } from './types';

type AudioScrubberProps = {
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  onEnded?: () => void;
  onPlay?: () => void;
  preload?: 'auto' | 'metadata' | 'none';
  src: string;
  trackLength?: number;
  trackingId?: string;
  autoSize?: boolean;
  className?: string;
  singlePlayer?: boolean;
  tabIndex?: number;
  showCurrentTime?: boolean;
  alwaysShowProgressButton?: boolean;
  isCompactMode?: boolean;
};

// Container styles - static classes for autoSize variations
const containerGridStyle = css`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
`;

const containerInlineFlexStyle = css`
  display: inline-flex;
  align-items: center;
`;

// Loader padding style
const loaderPaddingStyle = css`
  padding: ${theme.spacing(0.5)};
`;

// Input container base style
const inputContainerBaseStyle = css`
  align-items: center;
  display: inline-flex;
  height: ${theme.spacing(3)};
  margin: ${theme.spacing(0, 2, 0, 0.5)};
`;

// Text styles
const textBaseStyle = css`
  margin: 0;
  padding-right: ${theme.spacing(1)};
  font-size: ${theme.fontSize(14)};
`;

const textCompactStyle = css`
  margin: 0;
  padding-right: ${theme.spacing(1)};
  font-size: ${theme.fontSize(12)};
`;

const spinningLoaderStyle = css`
  display: block;
`;

const audioElementStyle = css`
  max-width: 100%;
`;

function getTime(time: number) {
  if (!isNaN(time)) {
    return Math.floor(time / 60) + ':' + ('0' + Math.floor(time % 60)).slice(-2);
  }

  return '0:00';
}

const initialState = {
  isPlaying: false,
  isLoaded: false,
  currentTime: 0,
  duration: 0,
  hasError: false,
  audioEnd: false,
};

const reducer = (state: AudioState, action): AudioState => {
  switch (action.type) {
    case 'isPlaying':
      return { ...state, isPlaying: action.payload };
    case 'currentTime':
      return { ...state, currentTime: action.payload };
    case 'duration':
      return { ...state, duration: action.payload };
    case 'isLoaded':
      return { ...state, isLoaded: action.payload };
    case 'hasError':
      return { ...state, hasError: action.payload };
    case 'audioEnd':
      return { ...state, isPlaying: false, currentTime: 0 };
    case 'reset':
      return { ...state, ...initialState };
    default:
      return state;
  }
};

export const AudioScrubber = ({
  autoplay = false,
  loop = false,
  muted = false,
  onEnded,
  onPlay,
  preload = 'metadata',
  src,
  singlePlayer,
  trackLength = 138,
  trackingId = 'audioScrubber',
  autoSize,
  tabIndex = 0,
  showCurrentTime = false,
  alwaysShowProgressButton = false,
  isCompactMode = false,
  className,
  ...rest
}: AudioScrubberProps) => {
  const audioElement = useRef<HTMLAudioElement>(null);
  const hasLoaded = useRef(false); // To prevent multiple calls to onCanPlay
  const [{ isLoaded, isPlaying, currentTime, duration, hasError }, dispatch] = useReducer(reducer, initialState);
  const onPlayClick = () => {
    if (audioElement && audioElement.current && !isPlaying) {
      dispatch({ type: 'isPlaying', payload: true });
      audioElement.current.play();
    }
  };

  const onPauseClick = () => {
    if (audioElement && audioElement.current && isPlaying) {
      dispatch({ type: 'isPlaying', payload: false });
      audioElement.current.pause();
    }
  };

  const pauseAllAudioPlayersExceptThis: React.ReactEventHandler<HTMLAudioElement> = (e) => {
    const audioPauseButtons = Array.from(document.querySelectorAll('[data-scrubber-pause]')) as HTMLButtonElement[];

    if (audioPauseButtons.length <= 1) return;

    audioPauseButtons.forEach((pauseButton) => {
      const audioElement = pauseButton.closest('[data-audio-scrubber]')?.querySelector('audio');
      if (audioElement != e.currentTarget) {
        pauseButton.click();
      }
    });
  };

  const onAudioEnd = () => {
    setTimeout(() => {
      dispatch({ type: 'audioEnd' });
    }, 100);

    if (onEnded) {
      onEnded();
    }
  };

  const onAudioPlay = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    if (singlePlayer) {
      pauseAllAudioPlayersExceptThis(e);
    }
    if (onPlay) {
      onPlay();
    }
  };

  const onCurrentTimeChange = (e) => {
    const time = +e.target.value;

    if (audioElement && audioElement.current) {
      dispatch({ type: 'currentTime', payload: time });
      audioElement.current.currentTime = time;
    }
  };

  // Calculate progress percentage for CSS variable
  let percent = 0;
  if (duration > 0) {
    percent = (currentTime / duration) * 100;
  }

  // Determine track style based on whether there's progress
  const hasProgress = duration > 0 && currentTime > 0;

  useEffect(() => {
    dispatch({ type: 'reset' });
    if (autoplay) {
      dispatch({ type: 'isPlaying', payload: true });
    }
    hasLoaded.current = false;
  }, [src]);

  const handleAudioLoad = () => {
    if (!hasLoaded.current) {
      dispatch({ type: 'isLoaded', payload: true });
      dispatch({ type: 'hasError', payload: false });
      hasLoaded.current = true;
    }
  };

  return (
    <div
      data-audio-scrubber
      className={cx(autoSize ? containerGridStyle : containerInlineFlexStyle, className)}
      onClick={(e) => {
        e.stopPropagation();
      }}
      {...rest}
    >
      {isLoaded ? (
        isPlaying ? (
          <IconButton
            tabIndex={tabIndex}
            data-scrubber-pause
            size={isCompactMode ? 'small' : 'normal'}
            label='Pause'
            onClick={onPauseClick}
            trackingId={`${trackingId}-btn-pause`}
          >
            <PauseIcon color='default' />
          </IconButton>
        ) : (
          <IconButton
            tabIndex={tabIndex}
            label='Play'
            size={isCompactMode ? 'small' : 'normal'}
            onClick={onPlayClick}
            disabled={hasError}
            trackingId={`${trackingId}-btn-play`}
          >
            <PlayIcon color='light' />
          </IconButton>
        )
      ) : hasError ? (
        <IconButton tabIndex={tabIndex} label='Play' disabled={true}>
          <PlayIcon color='light' />
        </IconButton>
      ) : (
        <div className={loaderPaddingStyle}>
          <SpinningLoader className={spinningLoaderStyle} size='small' />
        </div>
      )}
      <div
        className={cx('audio-scrubber-input-container', inputContainerBaseStyle)}
        style={!autoSize ? { width: `${trackLength}px` } : undefined}
      >
        <input
          tabIndex={tabIndex}
          className={cx(
            audioScrubberBaseStyle,
            hasProgress ? filledTrackStyle : emptyTrackStyle,
            alwaysShowProgressButton && thumbVisibleStyle,
            hasError && errorStyle,
            isCompactMode && compactStyle
          )}
          style={{ ['--audio-progress' as any]: percent + '%' }}
          disabled={hasError}
          onChange={onCurrentTimeChange}
          type='range'
          min='0'
          step='0.000001'
          max={duration}
          value={currentTime}
        />
      </div>
      <Text className={isCompactMode ? textCompactStyle : textBaseStyle}>
        {hasError ? 'N/A' : (showCurrentTime ? getTime(currentTime) + '/' : '') + getTime(duration)}
      </Text>
      <audio
        ref={audioElement}
        className={audioElementStyle}
        onCanPlay={handleAudioLoad}
        onLoadedMetadata={handleAudioLoad}
        onPlay={onAudioPlay}
        onEnded={onAudioEnd}
        onError={() => dispatch({ type: 'hasError', payload: true })}
        onLoad={() => dispatch({ type: 'isLoaded', payload: true })}
        onTimeUpdate={(e: any) => dispatch({ type: 'currentTime', payload: e.target.currentTime })}
        onDurationChange={(e: any) => {
          let duration = e.target.duration;
          if (e.target.duration === Infinity) {
            // for some reason for media file with length less than 0 seconds, it's coing as infinity so overrding it to 0
            duration = 0;
          }
          dispatch({ type: 'duration', payload: duration });
        }}
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
        preload={preload}
        src={src}
      >
        Your browser does not support the audio tag.
      </audio>
    </div>
  );
};
